import { defineStore } from 'pinia';
import { Risk } from '../../../riskMap/types/Risk';
import { RiskField } from '../../../riskMap/types/RiskField';
import { RiskMapRequest } from '../services/RiskMapRequest';
import { RiskMapReadResponse } from '../services/RiskMapResponses';

/**
 * The expected column headers for the risk import spreadsheet.
 */
export const EXPECTED_IMPORT_HEADERS: string[] = [
  'description',
  'likelihood',
  'severity',
  'residualLikelihood',
  'residualSeverity'
];

export interface ProcessImportHeadersResult {
  /** True when all expected headers were matched automatically and no manual mapping is needed. */
  autoMapped: boolean;
  /** Non-empty header strings found in the first row of rawData. */
  userHeaders: string[];
  /** Mapping from each expected header to the matched user header (empty string if unmatched). */
  headerMapping: Record<string, string>;
}

export interface ParseImportDataResult {
  /** Successfully parsed risks ready to be imported. */
  risks: Omit<Risk, 'id'>[];
  /** Optional warning or error message (e.g. skipped rows). */
  errorMessage?: string;
}

/**
 * Processes raw spreadsheet data to extract and match column headers.
 *
 * If every expected header has an exact case-insensitive match the mapping is
 * built automatically (`autoMapped: true`).  Otherwise a best-guess mapping is
 * returned for the user to review (`autoMapped: false`).
 *
 * @param rawData - 2-D array from the spreadsheet (first row = headers).
 */
export function processImportHeaders(rawData: any[][]): ProcessImportHeadersResult {
  const headers = rawData[0].map((cell: any) => String(cell || '').trim());
  const userHeaders = headers.filter((h: string) => h !== '');

  const headersMatch = EXPECTED_IMPORT_HEADERS.every(expected =>
    userHeaders.some(user => user.toLowerCase() === expected.toLowerCase())
  );

  const headerMapping: Record<string, string> = {};

  if (headersMatch) {
    EXPECTED_IMPORT_HEADERS.forEach(expected => {
      const matchingHeader = userHeaders.find(
        user => user.toLowerCase() === expected.toLowerCase()
      );
      if (matchingHeader) {
        headerMapping[expected] = matchingHeader;
      }
    });
    return { autoMapped: true, userHeaders, headerMapping };
  } else {
    EXPECTED_IMPORT_HEADERS.forEach(expected => {
      // First try exact case-insensitive match
      let matchingHeader = userHeaders.find(
        user => user.toLowerCase() === expected.toLowerCase()
      );
      // If no exact match, try word-based matching
      if (!matchingHeader) {
        const expectedWords = expected.split(/(?=[A-Z])/).map(w => w.toLowerCase());
        matchingHeader = userHeaders.find(user => {
          const userWords = user.toLowerCase().split(/[\s_-]+/);
          return expectedWords.some(ew => userWords.some(uw => uw === ew));
        });
      }
      headerMapping[expected] = matchingHeader || '';
    });
    return { autoMapped: false, userHeaders, headerMapping };
  }
}

/**
 * Parses raw spreadsheet rows into Risk objects using the provided header mapping.
 *
 * Rows that are entirely empty or contain invalid / out-of-range values are
 * skipped and reported in the returned `errorMessage`.
 *
 * @param rawData      - 2-D array from the spreadsheet (first row = headers).
 * @param headerMapping - Maps each expected header name to the user-supplied column header.
 */
export function parseImportData(
  rawData: string[][],
  headerMapping: Record<string, string>
): ParseImportDataResult {
  const allMapped = EXPECTED_IMPORT_HEADERS.every(header => headerMapping[header]);
  if (!allMapped) {
    return { risks: [], errorMessage: 'Please map all required headers' };
  }

  // Resolve the column index for each expected header
  const headerRow = rawData[0];
  const columnIndices: Record<string, number> = {};
  EXPECTED_IMPORT_HEADERS.forEach(expected => {
    const userHeader = headerMapping[expected];
    const normalizedUserHeader = String(userHeader || '').trim().toLowerCase();
    const index = headerRow.findIndex(
      (h: string) => String(h || '').trim().toLowerCase() === normalizedUserHeader
    );
    if (index !== -1) {
      columnIndices[expected] = index;
    }
  });

  const risks: Omit<Risk, 'id'>[] = [];
  const skippedRows: string[] = [];

  for (let i = 1; i < rawData.length; i++) {
    const row = rawData[i];

    // Skip empty rows
    if (row.every((cell: string) => !cell || String(cell).trim() === '')) {
      continue;
    }

    const rowIndex = i;
    const description = String(row[columnIndices.description] || '').trim();
    const likelihood = parseInt(row[columnIndices.likelihood]);
    const severity = parseInt(row[columnIndices.severity]);
    const residualLikelihood = parseInt(row[columnIndices.residualLikelihood]);
    const residualSeverity = parseInt(row[columnIndices.residualSeverity]);

    if (!description || isNaN(likelihood) || isNaN(severity) ||
        isNaN(residualLikelihood) || isNaN(residualSeverity)) {
      skippedRows.push(`Row ${i + 1}: Missing required values`);
      continue;
    }

    if (likelihood < 1 || likelihood > 4 || severity < 1 || severity > 4 ||
        residualLikelihood < 1 || residualLikelihood > 4 || residualSeverity < 1 || residualSeverity > 4) {
      skippedRows.push(`Row ${i + 1}: Likelihood/Severity values must be between 1 and 4`);
      continue;
    }

    risks.push({ index: rowIndex, description, likelihood, severity, residualLikelihood, residualSeverity });
  }

  if (risks.length === 0) {
    let errorMessage = 'No valid risks found in the data';
    if (skippedRows.length > 0) {
      errorMessage += '\n\nSkipped rows:\n' + skippedRows.join('\n');
    }
    return { risks: [], errorMessage };
  }

  let errorMessage: string | undefined;
  if (skippedRows.length > 0) {
    errorMessage = `Warning: ${skippedRows.length} row(s) were skipped due to validation errors`;
  }

  return { risks, errorMessage };
}

function parseFieldValue(field: RiskField, value: string): string | number | null {
  if (field === RiskField.Description) return value;
  const num = parseInt(value, 10);
  if (field === RiskField.Index) return num >= 1 ? num : null;
  if ([RiskField.Likelihood, RiskField.Severity, RiskField.ResidualLikelihood, RiskField.ResidualSeverity].includes(field)) {
    return num >= 1 && num <= 4 ? num : null;
  }
  return null;
}

export const useRiskMapStore = (request: RiskMapRequest) => defineStore('riskMapStore', {
  state: () => ({
    editingRisk: null as { id: string; field: RiskField } | null,
    riskMap: null as RiskMapReadResponse | null,
    risks: [] as Risk[],
    isImportWizardOpen: false,
    loading: false,
    error: null as string | null,
    currentMapId: null as string | null,
  }),
  


  getters: {
    getRiskLevelClass: () => (severity: number, likelihood: number) => {
      const riskLevel = severity * likelihood;
      
      if (riskLevel <= 4) return 'low-risk';
      if (riskLevel <= 8) return 'medium-risk';
      return 'high-risk';
    },

    getRiskColorClass: () => (likelihood: number, severity: number) => {
      const riskLevel = severity * likelihood;
      
      if (riskLevel <= 4) return 'color-low';
      if (riskLevel <= 8) return 'color-medium';
      return 'color-high';
    },

    /**
     * Returns a complete map of risk markers organized by severity-likelihood combinations
     */
    riskMarkersMap: (state) => {
      const markers: { [key: string]: string[] } = {};
      
      // Initialize all possible combinations
      for (let severity = 1; severity <= 4; severity++) {
        for (let likelihood = 1; likelihood <= 4; likelihood++) {
          const key = `${severity}-${likelihood}`;
          markers[key] = [];
        }
      }
      
      // Populate with actual risk data
      state.risks.forEach(risk => {
        const key = `${risk.severity}-${risk.likelihood}`;
        if (markers[key]) {
          markers[key].push("R" + risk.index);
        }
      });
      
      return markers;
    },

    /**
     * Returns a complete map of residual risk markers organized by severity-likelihood combinations
     */
    residualRiskMarkersMap: (state) => {
      const markers: { [key: string]: string[] } = {};
      
      // Initialize all possible combinations
      for (let severity = 1; severity <= 4; severity++) {
        for (let likelihood = 1; likelihood <= 4; likelihood++) {
          const key = `${severity}-${likelihood}`;
          markers[key] = [];
        }
      }
      
      // Populate with actual residual risk data
      state.risks.forEach(risk => {
        const key = `${risk.residualSeverity}-${risk.residualLikelihood}`;
        if (markers[key]) {
          markers[key].push("R" + risk.index);
        }
      });
      
      return markers;
    },

    getRiskById: (state) => (id: string) => {
      return state.risks.find(r => r.id === id);
    },

    getAllRisks: (state) => {
      return state.risks;
    },

    isLoading: (state) => state.loading,

    hasError: (state) => state.error !== null,

    getCurrentMapId: (state) => state.currentMapId
  },
  
  actions: {


    openImportWizard() {
      this.isImportWizardOpen = true;
    },

    closeImportWizard() {
      this.isImportWizardOpen = false;
    },
    
    startEdit(id: string, field: RiskField) {
      this.editingRisk = { id, field };
    },

    /**
     * Fetches risk map data from the API
     * @param mapId - The ID of the risk map to fetch
     */
    async fetchRiskMap(mapId: string) {
      this.loading = true;
      this.error = null;
      this.currentMapId = mapId;
      
      try {
        this.risks = await request.getRisks(mapId);
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch risk map';
        console.error(this.error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Adds a new risk
     * @param risk - The risk to add
     */
    async addRisk(risk: Omit<Risk, 'id'>) {
      if (!this.currentMapId) {
        this.error = 'No risk map selected';
        return;
      }

      this.loading = true;
      this.error = null;
      
      try {
        this.risks.push(await request.postRisk(this.currentMapId, risk));
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to add risk';
        console.error(this.error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Creates a default risk
     */
    async addDefaultRisk(): Promise<void> {
      await this.addRisk({
        index: 1,
        description: 'Nouveau risque',
        likelihood: 2,
        severity: 2,
        residualLikelihood: 2,
        residualSeverity: 2
      });
    },

    /**
     * Updates an existing risk
     * @param updatedRisk - The updated risk data
     */
    async updateRisk(updatedRisk: Risk) {
      if (!this.currentMapId) {
        this.error = 'No risk map selected';
        return;
      }

      this.loading = true;
      this.error = null;
      
      try {
        const updatedRiskFromApi = await request.updateRisk(this.currentMapId, updatedRisk.id, updatedRisk);
        const index = this.risks.findIndex(r => r.id === updatedRisk.id);
        if (index !== -1) {
          this.risks[index] = updatedRiskFromApi;
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to update risk';
        console.error(this.error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Deletes a risk by ID
     * @param riskId - The ID of the risk to delete
     */
    async deleteRisk(riskId: string) {
      if (!this.currentMapId) {
        this.error = 'No risk map selected';
        return;
      }

      this.loading = true;
      this.error = null;
      
      try {
        await request.deleteRisk(this.currentMapId, riskId);
        const index = this.risks.findIndex(r => r.id === riskId);
        if (index !== -1) {
          this.risks.splice(index, 1);
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to delete risk';
        console.error(this.error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Clears all risk data
     */
    clearRiskMap() {
      this.risks = [];
      this.riskMap = null;
      this.currentMapId = null;
      this.error = null;
    },

    /**
     * Bulk imports multiple risks at once via POST /riskMaps/:id
     * @param risks - Array of risks to import (without id for new risks)
     */
    async importRisks(risks: Omit<Risk, 'id'>[]) {
      if (!this.currentMapId) {
        this.error = 'No risk map selected';
        return;
      }

      this.loading = true;
      this.error = null;
      
      try {
        const postRisksResponse = await request.postRisks(this.currentMapId, risks);
        this.risks = this.risks.concat(postRisksResponse.risks);
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to bulk import risks';
        console.error(this.error);
        throw err;
      } finally {
        this.loading = false;
      }
    },



    /**
     * Updates a risk field with validation.
     * @param riskId - The ID of the risk to update
     * @param field - The field to update
     * @param value - The new value (string; parsed for numeric fields)
     */
    async updateRiskField(riskId: string, field: RiskField, value: string): Promise<void> {
      const risk = this.risks.find(r => r.id === riskId);
      if (!risk) return;
      const processed = parseFieldValue(field, value);
      if (processed === null) return;
      await this.updateRisk({ ...risk, [field]: processed });
      this.stopEdit();
    },

    stopEdit() {
      this.editingRisk = null;
    },
  }
});
